import { SectionCard } from './ui/SectionCard';
import { AlertCircle, AlertTriangle, Info, ShieldCheck } from 'lucide-react';
import type { Violation } from '@t212/shared';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/cn';

type Props = {
  violations: Violation[];
};

const sevIcon = {
  block: AlertCircle,
  warn: AlertTriangle,
  info: Info,
} as const;

const sevColor = {
  block: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
  warn: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  info: 'text-indigo-300 bg-indigo-500/10 border-indigo-500/30',
} as const;

export function ViolationsPanel({ violations }: Props) {
  const sorted = [...violations].sort((a, b) => {
    const rank = { block: 0, warn: 1, info: 2 } as const;
    return rank[a.severity] - rank[b.severity];
  });

  return (
    <SectionCard className="h-full">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-indigo-400" />
          <h3 className="text-sm font-medium text-zinc-200">Guard rails</h3>
        </div>
        <span className="text-[11px] text-zinc-400">{sorted.length} active</span>
      </div>

      {sorted.length === 0 ? (
        <div className="text-sm text-zinc-400 py-8 text-center border border-dashed border-zinc-800 rounded-lg">
          All rules pass.
        </div>
      ) : (
        <ul className="space-y-2">
          {sorted.map((v, i) => {
            const Icon = sevIcon[v.severity];
            const inner = (
              <div
                className={cn(
                  'flex items-start gap-2.5 p-2.5 rounded-lg border',
                  sevColor[v.severity],
                )}
              >
                <Icon className="size-4 mt-0.5 shrink-0" />
                <div className="text-xs leading-snug flex-1 min-w-0">
                  <div className="font-medium text-zinc-100">{v.ruleId}</div>
                  <div className="text-zinc-300 mt-0.5 text-pretty">{v.message}</div>
                </div>
              </div>
            );
            return (
              <li key={`${v.ruleId}-${i}`}>
                {v.ticker ? (
                  <Link
                    to="/positions/$ticker"
                    params={{ ticker: v.ticker }}
                    className="block hover:opacity-90 transition-opacity"
                  >
                    {inner}
                  </Link>
                ) : (
                  inner
                )}
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}
